/**
 * PayPal Payment Integration Component
 * 
 * Handles PayPal payment processing with PayPal SDK.
 * Provides PayPal button integration and payment confirmation.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '../Button';
import { LoadingSpinner } from '../LoadingSpinner';

/**
 * PayPal configuration
 */
interface PayPalConfig {
  clientId: string;
  currency: string;
  intent: 'capture' | 'authorize';
  environment: 'sandbox' | 'production';
}

/**
 * PayPal order details
 */
interface PayPalOrder {
  id: string;
  status: 'CREATED' | 'SAVED' | 'APPROVED' | 'VOIDED' | 'COMPLETED' | 'PAYER_ACTION_REQUIRED';
  amount: {
    currency_code: string;
    value: string;
  };
  payer?: {
    email_address: string;
    name?: {
      given_name: string;
      surname: string;
    };
  };
  purchase_units: Array<{
    reference_id: string;
    amount: {
      currency_code: string;
      value: string;
      breakdown?: {
        item_total?: { currency_code: string; value: string };
        tax_total?: { currency_code: string; value: string };
        shipping?: { currency_code: string; value: string };
      };
    };
    items?: Array<{
      name: string;
      unit_amount: { currency_code: string; value: string };
      quantity: string;
      category?: 'DIGITAL_GOODS' | 'PHYSICAL_GOODS';
    }>;
  }>;
}

/**
 * PayPal payment props
 */
interface PayPalPaymentProps {
  /** Payment amount in dollars */
  amount: number;
  /** Currency code (e.g., 'USD') */
  currency?: string;
  /** Order items for detailed breakdown */
  items?: Array<{
    name: string;
    price: number;
    quantity: number;
    category?: 'DIGITAL_GOODS' | 'PHYSICAL_GOODS';
  }>;
  /** Tax amount */
  tax?: number;
  /** Shipping amount */
  shipping?: number;
  /** Callback when payment succeeds */
  onSuccess: (order: PayPalOrder) => void;
  /** Callback when payment fails */
  onError: (error: string) => void;
  /** Callback when payment is cancelled */
  onCancel?: () => void;
  /** Custom CSS classes */
  className?: string;
  /** Customer information */
  customerInfo?: {
    email: string;
    name: string;
    phone?: string;
  };
  /** Order description */
  description?: string;
}

/**
 * Mock PayPal SDK for development
 * In production, this would be replaced with actual PayPal SDK
 */
class MockPayPalSDK {
  constructor(_config: PayPalConfig) {
    // Configuration parameter prefixed with underscore to indicate intentional non-use
  }

  /**
   * Create PayPal order
   */
  async createOrder(orderData: {
    intent: string;
    purchase_units: Array<{
      reference_id: string;
      amount: {
        currency_code: string;
        value: string;
        breakdown?: any;
      };
      items?: Array<{
        name: string;
        unit_amount: { currency_code: string; value: string };
        quantity: string;
        category?: 'DIGITAL_GOODS' | 'PHYSICAL_GOODS';
      }>;
    }>;
  }): Promise<PayPalOrder> {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      id: `PAY-${Date.now()}`,
      status: 'CREATED',
      amount: orderData.purchase_units[0].amount,
      purchase_units: orderData.purchase_units,
    };
  }

  /**
   * Capture PayPal payment
   */
  async capturePayment(orderId: string): Promise<PayPalOrder> {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Simulate success/failure (95% success rate)
    const success = Math.random() > 0.05;
    
    if (success) {
      return {
        id: orderId,
        status: 'COMPLETED',
        amount: {
          currency_code: 'USD',
          value: '100.00'
        },
        payer: {
          email_address: 'buyer@example.com',
          name: {
            given_name: 'John',
            surname: 'Doe'
          }
        },
        purchase_units: [{
          reference_id: 'default',
          amount: {
            currency_code: 'USD',
            value: '100.00'
          }
        }]
      };
    } else {
      throw new Error('Payment failed');
    }
  }

  /**
   * Render PayPal button
   */
  renderButton(container: HTMLElement, options: {
    createOrder: () => Promise<string>;
    onApprove: (data: { orderID: string }) => Promise<void>;
    onError: (error: any) => void;
    onCancel: () => void;
    style?: {
      layout?: 'vertical' | 'horizontal';
      color?: 'gold' | 'blue' | 'silver' | 'white' | 'black';
      shape?: 'rect' | 'pill';
      label?: 'paypal' | 'checkout' | 'buynow' | 'pay';
    };
  }): void {
    // Clear container
    container.innerHTML = '';
    
    // Create mock PayPal button
    const button = document.createElement('button');
    button.className = 'paypal-button';
    button.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #0070ba 0%, #003087 100%);
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        border: none;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.2s;
      ">
        <span>💳</span>
        <span>Pay with PayPal</span>
      </div>
    `;
    
    button.addEventListener('click', async () => {
      try {
        button.disabled = true;
        button.innerHTML = `
          <div style="
            background: linear-gradient(135deg, #0070ba 0%, #003087 100%);
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            border: none;
            font-size: 16px;
            font-weight: 600;
            cursor: not-allowed;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            opacity: 0.7;
          ">
            <span>⏳</span>
            <span>Processing...</span>
          </div>
        `;
        
        const orderID = await options.createOrder();
        
        // Simulate user approval
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        await options.onApprove({ orderID });
      } catch (error) {
        options.onError(error);
      } finally {
        button.disabled = false;
        button.innerHTML = `
          <div style="
            background: linear-gradient(135deg, #0070ba 0%, #003087 100%);
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            border: none;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          ">
            <span>💳</span>
            <span>Pay with PayPal</span>
          </div>
        `;
      }
    });
    
    container.appendChild(button);
  }
}

/**
 * PayPal button component
 */
interface PayPalButtonProps {
  paypalSDK: MockPayPalSDK;
  orderData: any;
  onSuccess: (order: PayPalOrder) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  disabled?: boolean;
}

const PayPalButton: React.FC<PayPalButtonProps> = ({
  paypalSDK,
  orderData,
  onSuccess,
  onError,
  onCancel,
  disabled = false,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!buttonRef.current || disabled || isInitialized) return;

    const initializeButton = async () => {
      try {
        paypalSDK.renderButton(buttonRef.current!, {
          createOrder: async () => {
            const order = await paypalSDK.createOrder(orderData);
            return order.id;
          },
          onApprove: async (data) => {
            try {
              const order = await paypalSDK.capturePayment(data.orderID);
              onSuccess(order);
            } catch (error) {
              onError('Payment capture failed');
            }
          },
          onError: (error) => {
            onError(error.message || 'PayPal payment failed');
          },
          onCancel: () => {
            onCancel();
          },
          style: {
            layout: 'vertical',
            color: 'blue',
            shape: 'rect',
            label: 'paypal',
          },
        });
        setIsInitialized(true);
      } catch (error) {
        onError('Failed to initialize PayPal button');
      }
    };

    initializeButton();
  }, [paypalSDK, orderData, onSuccess, onError, onCancel, disabled, isInitialized]);

  if (disabled) {
    return (
      <div className="w-full p-4 bg-gray-100 rounded-lg text-center text-gray-500">
        PayPal payment is currently disabled
      </div>
    );
  }

  return <div ref={buttonRef} className="w-full" />;
};

/**
 * Main PayPal Payment Component
 */
export const PayPalPayment: React.FC<PayPalPaymentProps> = ({
  amount,
  currency = 'USD',
  items = [],
  tax = 0,
  shipping = 0,
  onSuccess,
  onError,
  onCancel,
  className = '',
}) => {
  const [paypalSDK] = useState(() => new MockPayPalSDK({
    clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID || 'demo_client_id',
    currency,
    intent: 'capture',
    environment: 'sandbox'
  }));

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Prepare order data
  const orderData = {
    intent: 'CAPTURE',
    purchase_units: [{
      reference_id: 'default',
      amount: {
        currency_code: currency,
        value: amount.toFixed(2),
        breakdown: {
          item_total: {
            currency_code: currency,
            value: (amount - tax - shipping).toFixed(2),
          },
          tax_total: tax > 0 ? {
            currency_code: currency,
            value: tax.toFixed(2),
          } : undefined,
          shipping: shipping > 0 ? {
            currency_code: currency,
            value: shipping.toFixed(2),
          } : undefined,
        },
      },
      items: items.map(item => ({
        name: item.name,
        unit_amount: {
          currency_code: currency,
          value: item.price.toFixed(2),
        },
        quantity: item.quantity.toString(),
        category: (item.category as 'DIGITAL_GOODS' | 'PHYSICAL_GOODS') || 'PHYSICAL_GOODS',
      })),
    }],
  };

  useEffect(() => {
    // Simulate SDK loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleSuccess = useCallback((order: PayPalOrder) => {
    onSuccess(order);
  }, [onSuccess]);

  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    onError(errorMessage);
  }, [onError]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`p-6 text-center ${className}`}>
        <LoadingSpinner size="lg" />
        <p className="mt-3 text-gray-600">Loading PayPal...</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Pay with PayPal
        </h3>
        
        {/* Payment Amount */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Subtotal:</span>
              <span className="text-sm font-medium text-gray-900">
                ${(amount - tax - shipping).toFixed(2)} {currency}
              </span>
            </div>
            
            {tax > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Tax:</span>
                <span className="text-sm font-medium text-gray-900">
                  ${tax.toFixed(2)} {currency}
                </span>
              </div>
            )}
            
            {shipping > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Shipping:</span>
                <span className="text-sm font-medium text-gray-900">
                  ${shipping.toFixed(2)} {currency}
                </span>
              </div>
            )}
            
            <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
              <span className="text-base font-medium text-gray-900">Total:</span>
              <span className="text-lg font-semibold text-gray-900">
                ${amount.toFixed(2)} {currency}
              </span>
            </div>
          </div>
        </div>

        {/* Order Items */}
        {items.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg mb-6">
            <div className="p-4 border-b border-gray-200">
              <h4 className="font-medium text-gray-900">Order Items</h4>
            </div>
            <div className="divide-y divide-gray-200">
              {items.map((item, index) => (
                <div key={index} className="p-4 flex justify-between items-center">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900">{item.name}</h5>
                    <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* PayPal Button */}
        <div className="mb-6">
          <PayPalButton
            paypalSDK={paypalSDK}
            orderData={orderData}
            onSuccess={handleSuccess}
            onError={handleError}
            onCancel={handleCancel}
            disabled={!!error}
          />
        </div>

        {/* Alternative Actions */}
        <div className="flex justify-center">
          {onCancel && (
            <Button
              onClick={onCancel}
              variant="outline"
              size="sm"
            >
              Cancel Payment
            </Button>
          )}
        </div>

        {/* PayPal Benefits */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Why PayPal?</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Secure payment processing</li>
            <li>• Buyer protection included</li>
            <li>• Pay with your PayPal balance or linked accounts</li>
            <li>• No need to enter card details</li>
          </ul>
        </div>

        {/* PayPal Branding */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Powered by{' '}
            <span className="font-semibold text-blue-600">PayPal</span>
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * PayPal payment status component
 */
interface PayPalPaymentStatusProps {
  order: PayPalOrder;
  onContinue: () => void;
  onRetry?: () => void;
}

export const PayPalPaymentStatus: React.FC<PayPalPaymentStatusProps> = ({
  order,
  onContinue,
  onRetry,
}) => {
  const isSuccess = order.status === 'COMPLETED';
  const isPending = order.status === 'APPROVED' || order.status === 'PAYER_ACTION_REQUIRED';

  return (
    <div className="text-center space-y-4">
      {isSuccess && (
        <>
          <div className="text-green-600 text-6xl">✓</div>
          <h3 className="text-lg font-medium text-gray-900">Payment Successful!</h3>
          <p className="text-gray-600">
            Your PayPal payment of ${order.amount.value} has been processed successfully.
          </p>
          {order.payer && (
            <p className="text-sm text-gray-500">
              Paid by: {order.payer.email_address}
            </p>
          )}
          <p className="text-sm text-gray-500">
            Transaction ID: {order.id}
          </p>
          <Button onClick={onContinue} variant="primary" size="lg">
            Continue
          </Button>
        </>
      )}

      {isPending && (
        <>
          <div className="text-yellow-600 text-6xl">⏳</div>
          <h3 className="text-lg font-medium text-gray-900">Payment Pending</h3>
          <p className="text-gray-600">
            Your PayPal payment is being processed. This may take a few minutes.
          </p>
          <Button onClick={onContinue} variant="primary" size="lg">
            Continue
          </Button>
        </>
      )}

      {!isSuccess && !isPending && (
        <>
          <div className="text-red-600 text-6xl">✗</div>
          <h3 className="text-lg font-medium text-gray-900">Payment Failed</h3>
          <p className="text-gray-600">
            We couldn't process your PayPal payment. Please try again.
          </p>
          <div className="space-x-3">
            {onRetry && (
              <Button onClick={onRetry} variant="primary" size="lg">
                Try Again
              </Button>
            )}
            <Button onClick={onContinue} variant="outline" size="lg">
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PayPalPayment;